import { FormControl, InputAdornment, InputBase, styled } from '@mui/material';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import styles from './Inputs.module.css';
import DisplayErrorMessages from './DisplayErrorMessages';

const CustomInput = styled(InputBase)(
  ({ theme, error, disabled, multiline }) => ({
    'label + &': {
      marginTop: theme.spacing(3),
    },
    '&': {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: disabled ? 'hsl(0, 0%, 95%)' : 'white',
      border: '1px solid',
      borderColor: error ? theme.palette.error.main : '#E0E3E7',
      fontSize: 16,
      width: '100%',
      padding: multiline ? '10px' : '3px 12px',
      transition: theme.transitions.create([
        'border-color',
        'background-color',
        'box-shadow',
      ]),
      fontFamily: ['proxima-nova'].join(','),
      '&:focus': {
        borderColor: theme.palette.primary.main,
      },
    },
  }),
);

function Label({ label, required = false, error = false, subLabel = '' }) {
  return (
    <div className={cx(styles.label, { [styles.labelError]: error, error })}>
      {label.split('\n').map((l, index, array) => (
        <React.Fragment key={l}>
          {l}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      ))}
      {required && <span className={styles.required}>&#160;*</span>}
      {subLabel && (
        <>
          <br />
          <div className={cx(styles.subLabel)}>{subLabel}</div>
        </>
      )}
    </div>
  );
}

Label.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  error: PropTypes.bool,
  subLabel: PropTypes.string,
};

function TextField({
  inputName,
  label,
  formData,
  onChange,
  error = false,
  errorText = null,
  required = false,
  disabled = false,
  prefix = null,
  suffix = null,
  viewMode = false,
  type = 'text',
  multiline = false,
}) {
  if (!viewMode) {
    return (
      <FormControl variant="standard" fullWidth className={styles.formControl}>
        <Label label={label} required={required} error={error} />
        <CustomInput
          required={required}
          variant="outlined"
          id={`${inputName}-input`}
          data-testid={`${inputName}-input`}
          onChange={(e) => onChange(inputName, e.target.value)}
          error={error}
          disabled={disabled}
          // temporary fix for the Mui ResizeObserver error
          inputProps={{
            rows: multiline ? 4 : 1,
            multiline: multiline ? 4 : 1,
          }}
          type={type}
          value={formData[inputName] || ''}
          {...(prefix && {
            startAdornment: (
              <InputAdornment position="start">{prefix}</InputAdornment>
            ),
          })}
          {...(suffix && {
            endAdornment: (
              <InputAdornment position="end">{suffix}</InputAdornment>
            ),
          })}
        />
        <DisplayErrorMessages error={error} errorText={errorText} />
      </FormControl>
    );
  }
  return (
    <div className={styles.twoColumnLayout}>
      <div className={styles.labelColumn}>{label}</div>
      <div className={styles.valueColumn}>
        {prefix} {formData[inputName]} {suffix}
      </div>
    </div>
  );
}

TextField.propTypes = {
  inputName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  formData: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorText: PropTypes.arrayOf(PropTypes.string),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  viewMode: PropTypes.bool,
  type: PropTypes.string,
  multiline: PropTypes.bool,
};

export { TextField, Label };
